import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";
import plannerLogo from "../assets/logo/Logo-Planner-Turni.png";
import {
  ArrowDownTrayIcon,
  PlusIcon,
  UserCircleIcon,
  TrashIcon,
  PencilIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

const GruppiServizio = () => {
  const { user, activeCongregazione, updateGruppiServizioVisibilita } = useAuth();
  const [loading, setLoading] = useState(true);
  const [gruppi, setGruppi] = useState([]);
  const [volontari, setVolontari] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [viewingGroup, setViewingGroup] = useState(null);
  const [viewMembers, setViewMembers] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);
  const [visibilitySaving, setVisibilitySaving] = useState(false);
  const [visibilityEnabled, setVisibilityEnabled] = useState(false);
  const [formGroup, setFormGroup] = useState({
    nome: "",
    sorvegliante_id: "",
    assistente_id: "",
    gruppi_servizio_visibile: true,
  });
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [memberLookupQuery, setMemberLookupQuery] = useState("");
  const [memberLookupResults, setMemberLookupResults] = useState([]);
  const [memberLookupLoading, setMemberLookupLoading] = useState(false);
  const [memberLookupError, setMemberLookupError] = useState("");
  const isAdminOrSuper = user?.ruolo === "admin" || user?.ruolo === "super_admin";
  const canViewGroups =
    isAdminOrSuper || Boolean(activeCongregazione?.gruppi_servizio_visibili);
  const privilegeBadges = useMemo(
    () => [
      {
        key: "anziano",
        label: "Anziano",
        short: "A",
        className: "bg-rose-100 text-rose-700",
        width: "w-5",
      },
      {
        key: "servitore_ministero",
        label: "Servitore di Ministero",
        short: "SM",
        className: "bg-indigo-100 text-indigo-700",
        width: "w-7",
      },
      {
        key: "pioniere_regolare",
        label: "Pioniere Regolare",
        short: "PR",
        className: "bg-amber-100 text-amber-700",
        width: "w-7",
      },
    ],
    []
  );
  const privilegeLabelByKey = useMemo(() => {
    const map = new Map();
    privilegeBadges.forEach((badge) => map.set(badge.key, badge.label));
    return map;
  }, [privilegeBadges]);
  const formatPrivileges = useCallback(
    (privilegi = []) => {
      if (!privilegi || !privilegi.length) return "";
      return privilegi
        .map((key) => privilegeLabelByKey.get(key) || key)
        .filter(Boolean)
        .join(", ");
    },
    [privilegeLabelByKey]
  );
  const formatPrivilegeTags = useCallback(
    (privilegi = []) => {
      if (!privilegi || !privilegi.length) return "";
      const set = new Set(privilegi);
      return privilegeBadges
        .filter((badge) => set.has(badge.key))
        .map((badge) => `[${badge.short}]`)
        .join("");
    },
    [privilegeBadges]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [api.get("/gruppi-servizio")];
      if (isAdminOrSuper) {
        requests.push(api.get("/volontari", { params: { limit: 1000 } }));
      }
      const [gRes, vRes] = await Promise.all(requests);
      setGruppi(gRes.data?.gruppi || []);
      if (isAdminOrSuper && vRes) {
        const allVol = vRes.data?.volontari || vRes.data?.items || vRes.data || [];
        setVolontari(allVol);
      } else {
        setVolontari([]);
      }
    } catch (error) {
      console.error("Errore caricamento gruppi:", error);
      toast.error("Impossibile caricare i gruppi di servizio");
    } finally {
      setLoading(false);
    }
  }, [isAdminOrSuper]);

  useEffect(() => {
    if (activeCongregazione?.id && canViewGroups) {
      loadData();
    }
  }, [activeCongregazione?.id, canViewGroups, loadData]);

  useEffect(() => {
    setVisibilityEnabled(Boolean(activeCongregazione?.gruppi_servizio_visibili));
  }, [activeCongregazione?.gruppi_servizio_visibili]);

  useEffect(() => {
    if (!isAdminOrSuper) return undefined;

    const query = memberLookupQuery.trim();
    if (query.length < 2) {
      setMemberLookupResults([]);
      setMemberLookupError("");
      setMemberLookupLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      setMemberLookupLoading(true);
      setMemberLookupError("");
      try {
        const response = await api.get("/gruppi-servizio/cerca-membro", {
          params: { q: query },
        });
        if (!cancelled) {
          setMemberLookupResults(response.data?.membri || []);
        }
      } catch (error) {
        console.error("Errore ricerca membro:", error);
        if (!cancelled) {
          setMemberLookupResults([]);
          setMemberLookupError(
            error.response?.data?.message || "Impossibile effettuare la ricerca"
          );
        }
      } finally {
        if (!cancelled) setMemberLookupLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [activeCongregazione?.id, isAdminOrSuper, memberLookupQuery]);

  const openModal = (group = null) => {
    if (group) {
      setEditingGroup(group);
      setFormGroup({
        nome: group.nome || "",
        sorvegliante_id: group.sorvegliante_id || "",
        assistente_id: group.assistente_id || "",
        gruppi_servizio_visibile: group.gruppi_servizio_visibile ?? true,
      });
      setMemberSearch("");
      loadMembers(group.id);
    } else {
      setEditingGroup(null);
      setFormGroup({
        nome: "",
        sorvegliante_id: "",
        assistente_id: "",
        gruppi_servizio_visibile: true,
      });
      setSelectedMembers([]);
      setMemberSearch("");
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingGroup(null);
    setFormGroup({
      nome: "",
      sorvegliante_id: "",
      assistente_id: "",
      gruppi_servizio_visibile: true,
    });
    setSelectedMembers([]);
  };

  const closeViewModal = () => {
    setViewingGroup(null);
    setViewMembers([]);
    setViewLoading(false);
  };

  const maleVolontari = useMemo(
    () => volontari.filter((v) => v.sesso === "M"),
    [volontari]
  );

  const isAvailableForEditingGroup = useCallback(
    (volontario) => {
      if (!volontario.group_id) return true;
      return Boolean(editingGroup) && Number(volontario.group_id) === Number(editingGroup.id);
    },
    [editingGroup]
  );

  const eligibleMaleVolontari = useMemo(
    () => maleVolontari.filter(isAvailableForEditingGroup),
    [isAvailableForEditingGroup, maleVolontari]
  );

  const selectedMembersList = useMemo(
    () =>
      volontari.filter((v) => selectedMembers.includes(v.id)),
    [volontari, selectedMembers]
  );

  const filteredVolontari = useMemo(() => {
    const term = memberSearch.trim().toLowerCase();
    const alreadySelected = new Set(selectedMembers);
    return volontari
      .filter((v) => !alreadySelected.has(v.id))
      .filter(isAvailableForEditingGroup)
      .filter((v) => {
        if (!term) return true;
        return `${v.nome} ${v.cognome}`.toLowerCase().includes(term);
      })
      .sort((a, b) => a.cognome.localeCompare(b.cognome));
  }, [volontari, memberSearch, selectedMembers, isAvailableForEditingGroup]);

  const toggleMember = (id) => {
    setSelectedMembers((prev) => {
      const set = new Set(prev);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return [...set];
    });
  };

  const handleSaveGroup = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        nome: formGroup.nome.trim(),
        sorvegliante_id: formGroup.sorvegliante_id || null,
        assistente_id: formGroup.assistente_id || null,
        gruppi_servizio_visibile: Boolean(formGroup.gruppi_servizio_visibile),
      };
      let groupId = editingGroup?.id;
      if (editingGroup) {
        await api.put(`/gruppi-servizio/${editingGroup.id}`, payload);
        toast.success("Gruppo aggiornato");
      } else {
        const res = await api.post("/gruppi-servizio", payload);
        groupId = res.data?.id || groupId;
        toast.success("Gruppo creato");
      }

      if (groupId) {
        await api.post(`/gruppi-servizio/${groupId}/membri`, {
          membri: selectedMembers,
        });
      }
      closeModal();
      loadData();
    } catch (error) {
      console.error("Errore salvataggio gruppo:", error);
      toast.error(error.response?.data?.message || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm("Eliminare il gruppo? I membri verranno scollegati.")) {
      return;
    }
    try {
      await api.delete(`/gruppi-servizio/${groupId}`);
      toast.success("Gruppo eliminato");
      loadData();
    } catch (error) {
      console.error("Errore eliminazione gruppo:", error);
      toast.error("Errore nell'eliminazione");
    }
  };

  const loadMembers = async (groupId, { forSelection = true, forView = false } = {}) => {
    try {
      const res = await api.get(`/gruppi-servizio/${groupId}/membri`);
      const list = res.data?.membri || [];
      const ids = list.map((m) => m.id);
      if (forSelection) setSelectedMembers(ids);
      if (forView) setViewMembers(list);
      return list;
    } catch (error) {
      console.error("Errore caricamento membri:", error);
      if (forSelection) setSelectedMembers([]);
      if (forView) setViewMembers([]);
    }
  };

  const openViewModal = async (group) => {
    setViewingGroup(group);
    setViewLoading(true);
    await loadMembers(group.id, { forSelection: false, forView: true });
    setViewLoading(false);
  };

  const buildPeopleList = (group, members) => {
    const rows = [];
    const used = new Set();
    const privilegeById = new Map(
      (members || []).map((member) => [member.id, member.privilegi || []])
    );
    if (group?.sorvegliante_nome) {
      rows.push({
        nome: group.sorvegliante_nome,
        cognome: group.sorvegliante_cognome || "",
        ruolo: "Sorvegliante",
        privilegi: formatPrivileges(privilegeById.get(group.sorvegliante_id)),
      });
      if (group.sorvegliante_id) used.add(group.sorvegliante_id);
    }
    if (group?.assistente_nome) {
      rows.push({
        nome: group.assistente_nome,
        cognome: group.assistente_cognome || "",
        ruolo: "Assistente",
        privilegi: formatPrivileges(privilegeById.get(group.assistente_id)),
      });
      if (group.assistente_id) used.add(group.assistente_id);
    }
    members
      .filter((m) => !used.has(m.id))
      .forEach((m) =>
        rows.push({
          nome: m.nome,
          cognome: m.cognome,
          ruolo: "Membro",
          privilegi: formatPrivileges(m.privilegi),
        })
      );
    return rows;
  };

  const exportCSV = (group, members) => {
    const rows = buildPeopleList(group, members);
    const header = ["Nome", "Cognome", "Ruolo", "Privilegi"];
    const csv = [header.join(";")]
      .concat(
        rows.map((r) => [r.nome, r.cognome, r.ruolo, r.privilegi || ""].join(";"))
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `gruppo_${group.nome || "servizio"}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportPDF = (group, members) => {
    const rows = buildPeopleList(group, members);
    const win = window.open("", "_blank");
    if (!win) return;
    const groupName = (group?.nome || "").trim();
    const displayName = groupName || "Gruppo di Servizio";
    const titleText = groupName ? `Gruppo di Servizio - ${groupName}` : "Gruppo di Servizio";
    const hasGroupName = Boolean(groupName);
    const sorveglianteName = group?.sorvegliante_nome
      ? `${group.sorvegliante_nome} ${group.sorvegliante_cognome || ""}`.trim()
      : "Nessuno";
    const assistenteName = group?.assistente_nome
      ? `${group.assistente_nome} ${group.assistente_cognome || ""}`.trim()
      : "Nessuno";
    const htmlRows = rows
      .map(
        (r) =>
          `<tr>
            <td class="cell-name">${r.nome} ${r.cognome}</td>
            <td class="cell-role">${r.ruolo}</td>
            <td class="cell-priv">${r.privilegi || ""}</td>
          </tr>`
      )
      .join("");
    win.document.write(`
      <html>
        <head>
          <title>${titleText}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@600;700&family=Manrope:wght@400;600;700&display=swap');
            :root {
              --ink: #1f2937;
              --muted: #6b7280;
              --line: #e5e7eb;
              --accent: #2563eb;
              --accent-2: #60a5fa;
              --accent-soft: #eff6ff;
              --accent-dark: #1e3a8a;
              --paper: #ffffff;
              --bg: #f7f7fb;
            }
            @page {
              size: A4 portrait;
              margin: 10mm;
            }
            * {
              box-sizing: border-box;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            body {
              margin: 0;
              padding: 18px;
              font-family: 'Manrope', 'Segoe UI', sans-serif;
              color: var(--ink);
              background: radial-gradient(circle at top left, #dbeafe 0, var(--bg) 45%),
                radial-gradient(circle at 85% 0, #bfdbfe 0, var(--bg) 40%);
            }
            h1 {
              font-family: 'Fraunces', 'Times New Roman', serif;
              font-size: 28px;
              margin: 6px 0 0;
              font-weight: 700;
            }
            .page {
              background: var(--paper);
              border: 1px solid var(--line);
              border-radius: 18px;
              padding: 26px;
              position: relative;
              overflow: hidden;
            }
            .page::before {
              content: "";
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              height: 6px;
              background: linear-gradient(90deg, var(--accent), var(--accent-2));
            }
            .header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              gap: 16px;
              margin-bottom: 18px;
            }
            .kicker {
              text-transform: uppercase;
              letter-spacing: 0.18em;
              font-size: 11px;
              color: var(--muted);
              font-weight: 600;
            }
            .meta {
              display: flex;
              flex-wrap: wrap;
              gap: 8px;
              margin-top: 12px;
            }
            .pill {
              background: var(--accent-soft);
              border: 1px solid #bfdbfe;
              color: var(--accent-dark);
              border-radius: 999px;
              padding: 6px 10px;
              font-size: 11px;
              font-weight: 600;
            }
            .brand {
              display: flex;
              align-items: center;
              gap: 10px;
            }
            .brand img {
              height: 30px;
            }
            .brand-text {
              display: flex;
              flex-direction: column;
              align-items: flex-end;
              gap: 2px;
            }
            .brand-title {
              font-size: 12px;
              text-transform: uppercase;
              letter-spacing: 0.14em;
              font-weight: 700;
              color: var(--muted);
            }
            .brand-subtitle {
              font-size: 11px;
              color: var(--muted);
            }
            .table-wrap {
              border: 1px solid var(--line);
              border-radius: 12px;
              overflow: hidden;
              background: #ffffff;
            }
            table {
              border-collapse: collapse;
              width: 100%;
              font-size: 12px;
            }
            thead th {
              text-align: left;
              background: linear-gradient(90deg, #eff6ff, #ffffff);
              font-weight: 700;
              text-transform: uppercase;
              letter-spacing: 0.1em;
              font-size: 10px;
              padding: 10px 12px;
              border-bottom: 1px solid var(--line);
            }
            tbody td {
              padding: 9px 12px;
              border-bottom: 1px solid #f1f5f9;
            }
            tbody tr:nth-child(even) {
              background: #f8fafc;
            }
            tbody tr:last-child td {
              border-bottom: none;
            }
            .cell-role {
              color: var(--accent-dark);
              font-weight: 600;
            }
            .cell-priv {
              color: var(--muted);
            }
          </style>
        </head>
        <body>
          <div class="page">
            <div class="header">
              <div>
                ${hasGroupName ? `<div class="kicker">Gruppo di Servizio</div>` : ""}
                <h1>${displayName}</h1>
                <div class="meta">
                  <span class="pill">Membri: ${rows.length}</span>
                  <span class="pill">Sorv.: ${sorveglianteName}</span>
                  <span class="pill">Ass.: ${assistenteName}</span>
                </div>
              </div>
              <div class="brand">
                <img src="${plannerLogo}" alt="Planner Turni" />
                <div class="brand-text">
                  <span class="brand-title">Planner Turni</span>
                  <span class="brand-subtitle">Gruppi di servizio</span>
                </div>
              </div>
            </div>
            <div class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Ruolo</th>
                    <th>Privilegi</th>
                  </tr>
                </thead>
                <tbody>${htmlRows}</tbody>
              </table>
            </div>
          </div>
        </body>
      </html>
    `);
    win.document.close();
    const finalizePrint = () => {
      win.focus();
      setTimeout(() => {
        win.print();
        win.close();
      }, 200);
    };
    const fontReady = win.document.fonts?.ready || Promise.resolve();
    const imagePromises = Array.from(win.document.images || []).map((img) =>
      img.decode().catch(() => undefined)
    );
    Promise.all([fontReady, ...imagePromises])
      .then(finalizePrint)
      .catch(finalizePrint);
  };

  const exportAllGroupsPDF = async () => {
    if (!gruppi.length) {
      toast.error("Nessun gruppo da esportare");
      return;
    }

    setExportingAll(true);
    try {
      const responses = await Promise.all(
        gruppi.map((group) => api.get(`/gruppi-servizio/${group.id}/membri`))
      );
      const membersByGroup = new Map();
      responses.forEach((response, index) => {
        const members = response.data?.membri || [];
        membersByGroup.set(gruppi[index].id, members);
      });

      const totalStats = {
        proclamatori: 0,
        pionieri: 0,
      };

      const groupCards = gruppi
        .map((group, index) => {
          const members = membersByGroup.get(group.id) || [];
          const sortedMembers = [...members].sort((a, b) => {
            const cognomeCompare = (a.cognome || "").localeCompare(b.cognome || "");
            if (cognomeCompare !== 0) return cognomeCompare;
            return (a.nome || "").localeCompare(b.nome || "");
          });

          const pioneeriCount = members.filter((member) =>
            (member.privilegi || []).includes("pioniere_regolare")
          ).length;
          totalStats.proclamatori += members.length;
          totalStats.pionieri += pioneeriCount;

          const membersHtml = sortedMembers
            .map((member) => {
              const privileges = member.privilegi || [];
              const isPioneer = privileges.includes("pioniere_regolare");
              const isLeader =
                privileges.includes("anziano") ||
                privileges.includes("servitore_ministero");
              const tags = formatPrivilegeTags(privileges);
              const className = isPioneer
                ? "member pioneer"
                : isLeader
                ? "member leader"
                : "member";
              return `<div class="${className}">${member.cognome} ${member.nome} ${tags}</div>`;
            })
            .join("");

          return `
            <div class="group-card">
              <div class="group-header">
                <div class="group-title">GRUPPO N.${index + 1}</div>
                <div class="group-leads">
                  <div>Sorv.: ${group.sorvegliante_nome || "Nessuno"} ${group.sorvegliante_cognome || ""}</div>
                  <div>Ass.: ${group.assistente_nome || "Nessuno"} ${group.assistente_cognome || ""}</div>
                </div>
              </div>
              <div class="group-body">
                ${membersHtml || "<div class='member empty'>Nessun membro</div>"}
              </div>
              <div class="group-footer">
                <div>Proclamatori: <strong>${members.length}</strong></div>
                <div class="detail">Pionieri Regolari: ${pioneeriCount}</div>
              </div>
            </div>
          `;
        })
        .join("");

      const congregazioneNome =
        activeCongregazione?.nome ||
        user?.congregazione_nome ||
        "Gruppi di Servizio";
      const titleBarText = `GRUPPI DI SERVIZIO ${congregazioneNome}`
        .trim()
        .toUpperCase();
      const generatedAt = new Date().toLocaleString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
      const titleText = " ";
      const win = window.open("", "_blank");
      if (!win) {
        toast.error("Popup bloccato dal browser");
        return;
      }

      win.document.write(`
        <html>
          <head>
            <title>${titleText}</title>
            <style>
              @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap');
              :root {
                --ink: #1f2937;
                --muted: #6b7280;
                --line: #d1d5db;
                --accent: #2563eb;
                --accent-dark: #1e40af;
              }
              @page {
                size: A4 portrait;
                margin: 8mm;
              }
              * {
                box-sizing: border-box;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              body {
                margin: 0;
                padding: 12px;
                font-family: 'Montserrat', 'Helvetica Neue', Arial, sans-serif;
                color: var(--ink);
                background: #ffffff;
              }
              .title-bar {
                background: var(--accent);
                color: #ffffff;
                text-align: center;
                font-weight: 700;
                font-size: 15px;
                padding: 7px 10px;
                border: 1px solid var(--accent-dark);
                margin-bottom: 10px;
                letter-spacing: 0.04em;
              }
              .groups-grid {
                display: grid;
                grid-template-columns: repeat(3, 1fr);
                gap: 10px;
              }
              .group-card {
                border: 1px solid #1f2937;
                break-inside: avoid;
                page-break-inside: avoid;
                display: flex;
                flex-direction: column;
                min-height: 215px;
              }
              .group-header {
                background: var(--accent);
                color: #ffffff;
                padding: 7px 10px;
                border-bottom: 1px solid var(--accent-dark);
              }
              .group-title {
                font-weight: 700;
                font-size: 12px;
                margin-bottom: 4px;
              }
              .group-leads {
                font-size: 11px;
                font-weight: 600;
                line-height: 1.3;
              }
              .group-body {
                padding: 8px 10px;
                font-size: 11px;
                line-height: 1.35;
                flex: 1;
              }
              .member {
                margin-bottom: 2px;
              }
              .member.pioneer {
                color: var(--accent);
                font-weight: 600;
              }
              .member.leader {
                font-weight: 600;
              }
              .member.empty {
                color: var(--muted);
              }
              .group-footer {
                padding: 6px 10px 8px;
                border-top: 1px solid #e5e7eb;
                font-size: 11px;
              }
              .group-footer .detail {
                color: var(--accent);
                font-weight: 600;
                margin-top: 2px;
              }
              .summary {
                margin-top: 10px;
                display: flex;
                justify-content: flex-end;
                gap: 16px;
                font-size: 12px;
                color: var(--accent);
                font-weight: 700;
              }
              .page-footer {
                margin-top: 8px;
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 12px;
                font-size: 10px;
                color: var(--muted);
              }
              .page-footer img {
                height: 18px;
                opacity: 0.85;
              }
            </style>
          </head>
          <body>
            <div class="title-bar">${titleBarText}</div>
            <div class="groups-grid">
              ${groupCards}
            </div>
            <div class="summary">
              <span>TOTALE PROCLAMATORI: ${totalStats.proclamatori}</span>
              <span>PIONIERI REGOLARI: ${totalStats.pionieri}</span>
            </div>
            <div class="page-footer">
              <span>${generatedAt} • Planner Turni</span>
              <img src="${plannerLogo}" alt="Planner Turni" />
            </div>
          </body>
        </html>
      `);
      win.document.close();
      const finalizePrint = () => {
        win.focus();
        setTimeout(() => {
          win.print();
          win.close();
        }, 200);
      };
      const fontReady = win.document.fonts?.ready || Promise.resolve();
      const imagePromises = Array.from(win.document.images || []).map((img) =>
        img.decode().catch(() => undefined)
      );
      Promise.all([fontReady, ...imagePromises])
        .then(finalizePrint)
        .catch(finalizePrint);
    } catch (error) {
      console.error("Errore export gruppi:", error);
      toast.error("Errore durante l'export dei gruppi");
    } finally {
      setExportingAll(false);
    }
  };

  const handleVisibilityToggle = async (enabled) => {
    setVisibilitySaving(true);
    try {
      await api.patch("/gruppi-servizio/visibilita", { enabled });
      updateGruppiServizioVisibilita(enabled);
      setVisibilityEnabled(enabled);
      toast.success(
        enabled
          ? "Gruppi visibili ai proclamatori"
          : "Gruppi visibili solo agli amministratori"
      );
    } catch (error) {
      console.error("Errore aggiornamento visibilità:", error);
      toast.error("Impossibile aggiornare la visibilità");
      setVisibilityEnabled(Boolean(activeCongregazione?.gruppi_servizio_visibili));
    } finally {
      setVisibilitySaving(false);
    }
  };

  const displayMembers = useMemo(() => {
    if (!viewingGroup) return [];
    const excludedIds = new Set(
      [viewingGroup.sorvegliante_id, viewingGroup.assistente_id].filter(Boolean)
    );
    return viewMembers.filter((m) => !excludedIds.has(m.id));
  }, [viewMembers, viewingGroup]);

  const sorveglianteDetails = useMemo(() => {
    if (!viewingGroup?.sorvegliante_id) return null;
    return viewMembers.find((m) => m.id === viewingGroup.sorvegliante_id) || null;
  }, [viewMembers, viewingGroup]);

  const assistenteDetails = useMemo(() => {
    if (!viewingGroup?.assistente_id) return null;
    return viewMembers.find((m) => m.id === viewingGroup.assistente_id) || null;
  }, [viewMembers, viewingGroup]);

  const renderPrivilegeBadges = useCallback(
    (privilegi = []) => {
      if (!privilegi || !privilegi.length) return null;
      const set = new Set(privilegi);
      return privilegeBadges
        .filter((badge) => set.has(badge.key))
        .map((badge) => (
          <span
            key={badge.key}
            className={`inline-flex items-center justify-center h-5 ${badge.width} rounded-full text-xs ${badge.className}`}
            title={badge.label}
          >
            {badge.short}
          </span>
        ));
    },
    [privilegeBadges]
  );

  const viewMemberCount = useMemo(() => {
    if (!viewingGroup) return 0;
    const ids = new Set(viewMembers.map((m) => m.id));
    if (viewingGroup.sorvegliante_id) ids.add(viewingGroup.sorvegliante_id);
    if (viewingGroup.assistente_id) ids.add(viewingGroup.assistente_id);
    return ids.size;
  }, [viewMembers, viewingGroup]);

  if (!canViewGroups) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg shadow p-4 border border-yellow-200 text-yellow-800">
          I gruppi di servizio non sono abilitati per la visualizzazione.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-3">
          <div className="h-8 w-1/2 bg-gray-200 rounded"></div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gruppi di Servizio</h1>
          <p className="text-sm text-gray-600">
            Crea e gestisci i gruppi, assegnando sorvegliante, assistente e membri.
          </p>
          <div className="accent-line mt-3" />
        </div>
        {isAdminOrSuper && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={exportAllGroupsPDF}
              className="inline-flex items-center px-4 py-2 border border-primary-200 text-primary-700 rounded-lg hover:bg-primary-50 transition-colors text-sm disabled:opacity-60"
              disabled={exportingAll || loading}
              title="Esporta tutti i gruppi"
            >
              <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
              {exportingAll ? "Export..." : "Export gruppi"}
            </button>
            <button
              onClick={() => openModal(null)}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Nuovo Gruppo
            </button>
          </div>
        )}
      </div>

      {isAdminOrSuper && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-gray-800">
              Visibilità gruppi per proclamatori
            </p>
            <p className="text-xs text-gray-600">
              Consenti ai proclamatori della congregazione di poter visionare il gruppo di servizio.
            </p>
          </div>
          <label className="inline-flex items-center gap-3 text-sm text-gray-700">
            <span className="text-xs">
              {visibilitySaving ? "Aggiorno..." : visibilityEnabled ? "Attivo" : "Disattivo"}
            </span>
            <span
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                visibilityEnabled ? "bg-primary-600" : "bg-gray-300"
              } ${visibilitySaving ? "opacity-60" : ""}`}
            >
              <input
                type="checkbox"
                checked={visibilityEnabled}
                onChange={(e) => handleVisibilityToggle(e.target.checked)}
                disabled={visibilitySaving}
                className="sr-only"
              />
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                  visibilityEnabled ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </span>
          </label>
        </div>
      )}

      {isAdminOrSuper && (
        <section className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm" aria-labelledby="cerca-membro-title">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <h2 id="cerca-membro-title" className="text-base font-semibold text-gray-900">
                Cerca membro
              </h2>
              <p className="text-sm text-gray-600">
                Cerca per nome, cognome o email e consulta anagrafica, privilegi e gruppo di servizio.
              </p>
            </div>
            <div className="w-full lg:max-w-md">
              <label htmlFor="cerca-membro" className="sr-only">
                Cerca un membro
              </label>
              <input
                id="cerca-membro"
                type="search"
                value={memberLookupQuery}
                onChange={(event) => setMemberLookupQuery(event.target.value)}
                placeholder="Nome, cognome o email"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {memberLookupQuery.trim().length === 1 && (
            <p className="mt-3 text-xs text-gray-500">Inserisci almeno 2 caratteri per avviare la ricerca.</p>
          )}
          {memberLookupLoading && (
            <p className="mt-3 text-sm text-gray-500">Ricerca in corso...</p>
          )}
          {memberLookupError && (
            <p className="mt-3 text-sm text-red-600" role="alert">{memberLookupError}</p>
          )}
          {!memberLookupLoading && !memberLookupError && memberLookupQuery.trim().length >= 2 && memberLookupResults.length === 0 && (
            <p className="mt-3 text-sm text-gray-500">Nessun membro trovato.</p>
          )}
          {memberLookupResults.length > 0 && (
            <div className="mt-4 grid gap-3 xl:grid-cols-2">
              {memberLookupResults.map((membro) => (
                <article key={membro.id} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h3 className="font-semibold text-gray-900">
                      {membro.nome} {membro.cognome}
                    </h3>
                    <span className={`text-xs font-medium ${membro.stato === "attivo" ? "text-green-700" : "text-gray-500"}`}>
                      {membro.stato === "attivo" ? "Attivo" : "Non attivo"}
                    </span>
                  </div>
                  <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-3 text-sm sm:grid-cols-2">
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Email</dt>
                      <dd className="break-words text-gray-800">{membro.email || "Non registrata"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Numero</dt>
                      <dd className="text-gray-800">{membro.telefono || "Non registrato"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Età</dt>
                      <dd className="text-gray-800">{membro.eta ?? "Non registrata"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Gruppo di servizio</dt>
                      <dd className="text-gray-800">{membro.gruppo_nome || "Nessun gruppo"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Conduttore</dt>
                      <dd className="text-gray-800">{membro.service_leader ? "Abilitato" : "Non abilitato"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Opera pubblica</dt>
                      <dd className="text-gray-800">{membro.postazioni_attivo ? "Abilitato" : "Non abilitato"}</dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Nomine e privilegi</dt>
                      <dd className="text-gray-800">
                        {(membro.privilegi || []).map((privilegio) => privilegio.label || privilegio).join(", ") || "Nessuna"}
                      </dd>
                    </div>
                  </dl>
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      {gruppi.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-6 text-center text-gray-600">
          Nessun gruppo presente. Crea il primo gruppo.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {gruppi.map((gruppo) => (
          <div
            key={gruppo.id}
            className="relative bg-white border border-gray-200 rounded-lg p-4 pl-6 shadow-sm flex flex-col gap-3 transition hover:bg-gray-50 hover:border-primary-200 hover:shadow-sm"
          >
            <span className="accent-bar top-4 bottom-4" aria-hidden="true" />
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{gruppo.nome}</h3>
                  <p className="text-sm text-gray-600">Membri: {gruppo.membri_count || 0}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => openViewModal(gruppo)}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    title="Dettagli gruppo"
                  >
                    <EyeIcon className="h-5 w-5" />
                  </button>
                  {isAdminOrSuper && (
                    <>
                      <button
                        onClick={() => openModal(gruppo)}
                        className="p-2 text-primary-600 hover:bg-primary-50 rounded transition-colors"
                        title="Modifica"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteGroup(gruppo.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Elimina"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="space-y-2 text-sm text-gray-700">
                <div className="flex items-center gap-2">
                  <UserCircleIcon className="h-4 w-4 text-gray-500" />
                  <span>
                    Sorvegliante:{" "}
                    {gruppo.sorvegliante_nome
                      ? `${gruppo.sorvegliante_nome} ${gruppo.sorvegliante_cognome || ""}`
                      : "Nessuno"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <UserCircleIcon className="h-4 w-4 text-gray-500" />
                  <span>
                    Assistente:{" "}
                    {gruppo.assistente_nome
                      ? `${gruppo.assistente_nome} ${gruppo.assistente_cognome || ""}`
                      : "Nessuno"}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 p-3 sm:p-4">
          <div className="flex min-h-full items-center justify-center">
            <div className="my-3 flex max-h-[calc(100vh-1.5rem)] max-h-[calc(100dvh-1.5rem)] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
              <form onSubmit={handleSaveGroup} className="flex min-h-0 flex-1 flex-col">
                <div className="min-h-0 overflow-y-auto p-4 sm:p-6 space-y-4">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">
                    {editingGroup ? "Modifica gruppo" : "Nuovo gruppo"}
                  </h2>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome gruppo <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formGroup.nome}
                    onChange={(e) => setFormGroup({ ...formGroup, nome: e.target.value })}
                    required
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    maxLength={255}
                  />
                </div>
                {isAdminOrSuper && (
                  <div className="border border-gray-200 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-800">
                        Visibilità del gruppo
                      </p>
                      <p className="text-xs text-gray-600">
                        Rendi visibile questo gruppo ai proclamatori (solo lettura).
                      </p>
                    </div>
                    <label className="inline-flex items-center gap-3 text-sm text-gray-700">
                      <span className="text-xs">{formGroup.gruppi_servizio_visibile ? "Visibile" : "Nascosto"}</span>
                      <span className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${formGroup.gruppi_servizio_visibile ? "bg-primary-600" : "bg-gray-300"}`}>
                        <input
                          type="checkbox"
                          checked={formGroup.gruppi_servizio_visibile}
                          onChange={(e) =>
                            setFormGroup({
                              ...formGroup,
                              gruppi_servizio_visibile: e.target.checked,
                            })
                          }
                          className="sr-only"
                        />
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${formGroup.gruppi_servizio_visibile ? "translate-x-6" : "translate-x-1"}`} />
                      </span>
                    </label>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sorvegliante (uomo)
                    </label>
                    <select
                      value={formGroup.sorvegliante_id}
                      onChange={(e) => setFormGroup({ ...formGroup, sorvegliante_id: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Nessuno</option>
                      {eligibleMaleVolontari.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.nome} {v.cognome}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Assistente (uomo)
                    </label>
                    <select
                      value={formGroup.assistente_id}
                      onChange={(e) => setFormGroup({ ...formGroup, assistente_id: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Nessuno</option>
                      {eligibleMaleVolontari.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.nome} {v.cognome}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="border-t pt-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Aggiungi proclamatori
                    </label>
                    <p className="mb-2 text-xs text-gray-500">
                      Sono disponibili solo i proclamatori non già assegnati a un altro gruppo.
                    </p>
                    <input
                      type="text"
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      placeholder="Cerca per nome o cognome"
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <div className="mt-2 max-h-40 overflow-y-auto border rounded p-2 space-y-1 bg-gray-50">
                      {filteredVolontari.length === 0 ? (
                        <p className="text-xs text-gray-500">Nessun risultato</p>
                      ) : (
                        filteredVolontari.slice(0, 12).map((v) => (
                          <div key={v.id} className="flex items-center justify-between text-sm text-gray-700">
                            <span>{`${v.nome} ${v.cognome}`}</span>
                            <button
                              type="button"
                              onClick={() => toggleMember(v.id)}
                              className="text-primary-600 text-xs font-semibold hover:underline"
                            >
                              Aggiungi
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Membri selezionati</span>
                      <span className="text-xs text-gray-500">{selectedMembers.length} selezionati</span>
                    </div>
                    {selectedMembersList.length === 0 ? (
                      <p className="text-xs text-gray-500 mt-1">Nessun membro aggiunto.</p>
                    ) : (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {selectedMembersList.map((v) => (
                          <span
                            key={v.id}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-primary-50 text-primary-700 rounded-full text-xs border border-primary-100"
                          >
                            {v.nome} {v.cognome}
                            <button
                              type="button"
                              onClick={() => toggleMember(v.id)}
                              className="text-primary-600 hover:text-primary-800"
                              aria-label="Rimuovi"
                            >
                              ✕
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                </div>
                <div className="flex shrink-0 justify-end gap-2 border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:border-gray-400"
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 text-sm rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
                  >
                    {saving ? "Salvataggio..." : "Salva"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {viewingGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full">
            <div className="p-4 sm:p-6 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{viewingGroup.nome}</h2>
                  <div className="mt-2 space-y-3">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-primary-100 text-primary-700 text-xs">
                          S
                        </span>
                        <span>
                          Sorvegliante:{" "}
                          {viewingGroup.sorvegliante_nome
                            ? `${viewingGroup.sorvegliante_nome} ${viewingGroup.sorvegliante_cognome || ""}`
                            : "Nessuno"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center h-5 w-7 rounded-full bg-blue-100 text-blue-700 text-xs">
                          AS
                        </span>
                        <span>
                          Assistente:{" "}
                          {viewingGroup.assistente_nome
                            ? `${viewingGroup.assistente_nome} ${viewingGroup.assistente_cognome || ""}`
                            : "Nessuno"}
                        </span>
                      </div>
                    </div>
                    <div className="h-px bg-gray-200" />
                    <div className="flex flex-wrap md:flex-nowrap items-center gap-x-4 gap-y-2 md:gap-y-0 text-xs text-gray-600">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-rose-100 text-rose-700 text-xs">
                          A
                        </span>
                        <span className="whitespace-nowrap">Anziano</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center h-5 w-7 rounded-full bg-indigo-100 text-indigo-700 text-xs">
                          SM
                        </span>
                        <span className="whitespace-nowrap">Servitore di Ministero</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center h-5 w-7 rounded-full bg-amber-100 text-amber-700 text-xs">
                          PR
                        </span>
                        <span className="whitespace-nowrap">Pioniere Regolare</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-gray-200 text-gray-700 text-xs">
                          M
                        </span>
                        <span className="whitespace-nowrap">Membri del gruppo</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => exportCSV(viewingGroup, viewMembers)}
                    className="px-3 py-1.5 text-xs font-semibold rounded border border-gray-300 hover:bg-gray-50"
                  >
                    Esporta CSV
                  </button>
                  <button
                    onClick={() => exportPDF(viewingGroup, viewMembers)}
                    className="px-3 py-1.5 text-xs font-semibold rounded border border-gray-300 hover:bg-gray-50"
                  >
                    Esporta PDF
                  </button>
                </div>
              </div>

              <div className="border rounded-lg p-3 bg-gray-50">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">Membri del gruppo ({viewMemberCount})</h4>
                {viewLoading ? (
                  <p className="text-sm text-gray-500">Caricamento...</p>
                ) : (
                  <div className="space-y-2 text-sm text-gray-800 max-h-72 overflow-y-auto">
                    {viewingGroup.sorvegliante_nome && (
                      <div className="flex items-center gap-2">
                        <span>{viewingGroup.sorvegliante_nome} {viewingGroup.sorvegliante_cognome || ""}</span>
                        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-primary-100 text-primary-700 text-xs">S</span>
                        {renderPrivilegeBadges(sorveglianteDetails?.privilegi)}
                      </div>
                    )}
                    {viewingGroup.assistente_nome && (
                      <div className="flex items-center gap-2">
                        <span>{viewingGroup.assistente_nome} {viewingGroup.assistente_cognome || ""}</span>
                        <span className="inline-flex items-center justify-center h-5 w-7 rounded-full bg-blue-100 text-blue-700 text-xs">AS</span>
                        {renderPrivilegeBadges(assistenteDetails?.privilegi)}
                      </div>
                    )}
                    {displayMembers.length === 0 ? (
                      <p className="text-sm text-gray-500">Nessun membro assegnato.</p>
                    ) : (
                      displayMembers.map((m) => (
                        <div key={m.id} className="flex items-center gap-2">
                          <span>{m.nome} {m.cognome}</span>
                          <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-gray-200 text-gray-700 text-xs">M</span>
                          {renderPrivilegeBadges(m.privilegi)}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  onClick={closeViewModal}
                  className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:border-gray-400"
                >
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GruppiServizio;
